(function () {
  "use strict";

  if (window.__agentForceOverlayBoot) {
    window.__agentForceOverlayBoot(document);
    return;
  }

  const DEFAULT_MONSTER_HP = 240;
  const PROBLEMS = [
    {
      id: "manual-work",
      label: "Manual work",
      short: "Copy-paste tasks and repetitive admin",
      spawn: [-54, 81],
      title: "Manual work converted into AI workflows.",
      body: "Kachersoft maps repeat work, proves one path on real records, and leaves your team with approval rules, a dashboard, and a runbook.",
      metrics: ["Workflow map", "Measured proof", "Runbook"],
      steps: [
        {
          title: "Map repeat work",
          body: "We trace copy-paste loops, owners, source systems, and decisions before changing the workflow.",
          output: "Workflow map"
        },
        {
          title: "Score automation fit",
          body: "Each task is checked for volume, risk, data access, and where a person still reviews.",
          output: "Fit scorecard"
        },
        {
          title: "Prove live cases",
          body: "One narrow path runs on real records, with time saved and error rate measured.",
          output: "Measured proof"
        },
        {
          title: "Route exceptions",
          body: "Stable work moves automatically; edge cases stay visible for human judgment.",
          output: "Approval rules"
        },
        {
          title: "Hand off runbook",
          body: "Your team keeps the dashboard, SOP, and operating notes.",
          output: "Runbook"
        }
      ]
    },
    {
      id: "workflow-chaos",
      label: "Workflow chaos",
      short: "Approvals, routing, and handoffs drift",
      spawn: [81, -54],
      title: "Workflow chaos turned into controlled routing.",
      body: "Kachersoft traces the handoffs, defines one operating path, and gives managers a live view of queues, blockers, and ownership.",
      metrics: ["Handoff map", "Routing rules", "Ops dashboard"],
      steps: [
        {
          title: "Trace the handoffs",
          body: "We find where work stalls between teams, tools, approvals, and missing owners.",
          output: "Handoff map"
        },
        {
          title: "Define the path",
          body: "Triggers, states, owners, exceptions, and approvals become one operating model.",
          output: "Routing rules"
        },
        {
          title: "Connect the tools",
          body: "CRM, inboxes, sheets, and task systems sync status without double entry.",
          output: "Tool sync"
        },
        {
          title: "Show the queue",
          body: "A live view exposes blockers, aging work, and handoffs before they become fire drills.",
          output: "Ops dashboard"
        },
        {
          title: "Set the cadence",
          body: "Reviews, escalation rules, and runbooks keep the workflow stable after launch.",
          output: "Review rhythm"
        }
      ]
    },
    {
      id: "automation-gap",
      label: "Automation gap",
      short: "Tools exist but work still waits",
      spawn: [-81, -27],
      title: "Automation gaps closed across the operation.",
      body: "Kachersoft finds the missing capability, chooses build-or-buy carefully, and pilots one workflow before wider rollout.",
      metrics: ["Decision brief", "Pilot proof", "Control rules"],
      steps: [
        {
          title: "Find the missing link",
          body: "We compare current tools against the workflow to find the highest-value automation gap.",
          output: "Gap map"
        },
        {
          title: "Choose build or buy",
          body: "Existing tools, custom logic, cost, lock-in, and risk are weighed first.",
          output: "Decision brief"
        },
        {
          title: "Pilot one workflow",
          body: "A contained flow runs on real data before the business case is expanded.",
          output: "Pilot proof"
        },
        {
          title: "Add control gates",
          body: "Approvals, logs, and fallbacks keep the agent inside trusted boundaries.",
          output: "Control rules"
        },
        {
          title: "Stage the rollout",
          body: "Owners, phases, metrics, and off-ramps make expansion controlled.",
          output: "Rollout plan"
        }
      ]
    },
    {
      id: "slow-performance",
      label: "Slow performance",
      short: "Slow systems and response loops",
      spawn: [108, 54],
      title: "Performance drag converted into measurable lift.",
      body: "Kachersoft benchmarks the real waits, ranks bottlenecks by impact, ships focused fixes, and hands over monitoring.",
      metrics: ["Baseline", "Speed patch", "Perf runbook"],
      steps: [
        {
          title: "Measure real waits",
          body: "We benchmark the screens, pages, APIs, and automations users actually wait on.",
          output: "Baseline"
        },
        {
          title: "Rank bottlenecks",
          body: "Theme weight, API latency, queries, and scripts are sorted by user impact.",
          output: "Bottleneck list"
        },
        {
          title: "Ship focused fixes",
          body: "We remove weight, cache safely, and tighten slow calls without breaking the workflow.",
          output: "Speed patch"
        },
        {
          title: "Retest under load",
          body: "Fixes are checked against real traffic patterns and production-like data.",
          output: "Retest report"
        },
        {
          title: "Monitor the system",
          body: "Alerts and runbooks show what to watch and what to fix first.",
          output: "Perf runbook"
        }
      ]
    },
    {
      id: "training-gaps",
      label: "Training gaps",
      short: "Knowledge stays scattered across people",
      spawn: [-108, -54],
      title: "Training gaps turned into guided support.",
      body: "Kachersoft turns real cases into role playbooks, approval standards, and enablement your managers can keep using.",
      metrics: ["Playbook set", "Live-case lab", "Enablement kit"],
      steps: [
        {
          title: "Map role needs",
          body: "We identify the few skills and decisions that change daily output for each role.",
          output: "Role map"
        },
        {
          title: "Write playbooks",
          body: "Best replies, exceptions, prompts, and rules become reusable team guides.",
          output: "Playbook set"
        },
        {
          title: "Train real cases",
          body: "Sessions use your tickets, records, and workflows, not generic demos.",
          output: "Live-case lab"
        },
        {
          title: "Set approval rules",
          body: "The team knows when to trust the agent, edit it, or escalate.",
          output: "Approval guide"
        },
        {
          title: "Leave enablement",
          body: "Managers keep the review cadence, dashboard cues, and training notes.",
          output: "Enablement kit"
        }
      ]
    },
    {
      id: "scattered-data",
      label: "Scattered data",
      short: "Decisions depend on fragmented context",
      spawn: [54, 108],
      title: "Scattered data turned into usable intelligence.",
      body: "Kachersoft identifies trusted sources, assigns ownership, fixes data paths, and proves the first answer on live records.",
      metrics: ["Source map", "Data proof", "Data runbook"],
      steps: [
        {
          title: "Inventory sources",
          body: "We identify which sheets, apps, docs, and databases are trusted for each workflow.",
          output: "Source map"
        },
        {
          title: "Assign ownership",
          body: "Owners, permissions, refresh cadence, and stale fields are documented.",
          output: "Ownership map"
        },
        {
          title: "Clean the paths",
          body: "Required fields, mappings, and sync rules are fixed before AI depends on them.",
          output: "Data rules"
        },
        {
          title: "Connect core systems",
          body: "APIs and exports move the needed context without creating another shadow tool.",
          output: "System links"
        },
        {
          title: "Prove with live data",
          body: "The first dashboard or agent answers against real records, with gaps logged plainly.",
          output: "Data proof"
        },
        {
          title: "Hand off governance",
          body: "Your team keeps source rules, refresh checks, and escalation steps.",
          output: "Data runbook"
        }
      ]
    }
  ];

  function initOverlay(root) {
    if (!root || root.__agentForceOverlayReady) return;

    const overlay = root.querySelector("[data-agent-force-overlay]");
    const canvas = root.querySelector("[data-city-canvas]");
    const hint = root.querySelector("[data-city-hint]");
    const problemList = root.querySelector("[data-problem-list]");
    const selectedLabel = root.querySelector("[data-selected-label]");
    const armButton = root.querySelector("[data-arm-drop]");
    const scrollButton = root.querySelector("[data-scroll-down]");
    const helper = root.querySelector("[data-helper-text]");
    const targetingLabel = root.querySelector("[data-targeting-label]");
    const outcome = root.querySelector("[data-outcome]");
    const outcomeTitle = root.querySelector("[data-outcome-title]");
    const outcomeBody = root.querySelector("[data-outcome-body]");
    const outcomeSimulation = root.querySelector("[data-outcome-simulation]");
    const outcomeSteps = root.querySelector("[data-outcome-steps]");
    const outcomeStepCount = root.querySelector("[data-outcome-step-count]");
    const metrics = Array.from(root.querySelectorAll("[data-outcome-metrics] span"));
    const resetButton = root.querySelector("[data-reset-overlay]");
    const cta = root.querySelector("[data-cta-link]");
    const solutionBrief = root.querySelector("[data-solution-brief]");
    const solutionThreat = root.querySelector("[data-solution-threat]");
    const solutionStepCount = root.querySelector("[data-solution-step-count]");
    const solutionTitle = root.querySelector("[data-solution-title]");
    const solutionBody = root.querySelector("[data-solution-body]");
    const solutionOutput = root.querySelector("[data-solution-output]");
    const solutionProgress = root.querySelector("[data-solution-progress]");

    if (!overlay || !canvas || !problemList) return;

    root.__agentForceOverlayReady = true;

    let selected = PROBLEMS[0];
    let activeKills = 0;
    let seenMonster = false;
    let pollTimer = 0;
    let readyTimer = 0;
    let cityScrollActive = false;
    let mapClickReopenedSelection = false;
    let renderedProgressSteps = 0;
    let threatScrollTimer = 0;
    let syncingThreatScroll = false;
    const listeners = [];

    function listen(target, type, listener, options) {
      if (!target) return;
      target.addEventListener(type, listener, options);
      listeners.push([target, type, listener, options]);
    }

    canvas.tabIndex = canvas.tabIndex >= 0 ? canvas.tabIndex : 0;
    listen(canvas, "wheel", guardCityWheel, { capture: true });
    listen(canvas, "pointerdown", handleCityPointerDown, { capture: true });
    listen(canvas, "pointerup", guardCompletedMapClick, { capture: true });
    listen(canvas, "click", guardCompletedMapClick, { capture: true });
    listen(canvas, "pointerup", handleCityPointerUp);
    listen(canvas, "keydown", handleCityKeydown);
    listen(armButton, "click", armDropMode);
    listen(scrollButton, "click", scrollPastHero);
    listen(resetButton, "click", reopenThreatSelection);
    listen(cta, "click", function () {
      emit(root, "agent-force:cta-clicked", selected);
    });

    renderProblems(problemList, selected.id, function (problem) {
      selected = problem;
      updateSelected();
    });
    listen(problemList, "scroll", handleThreatScroll, { passive: true });
    listen(problemList, "scrollend", selectCenteredThreat, { passive: true });
    listen(problemList, "pointerup", scheduleThreatSelection, { passive: true });
    listen(problemList, "touchend", scheduleThreatSelection, { passive: true });
    updateSelected();
    startReadyPoll();

    root.__agentForceOverlayDestroy = function () {
      stopPolling();
      if (threatScrollTimer) window.clearTimeout(threatScrollTimer);
      if (readyTimer) window.clearInterval(readyTimer);
      listeners.forEach(function (entry) {
        entry[0].removeEventListener(entry[1], entry[2], entry[3]);
      });
      root.classList.remove("is-targeting", "is-in-battle", "is-complete", "is-city-scroll-active");
      root.__agentForceOverlayReady = false;
      delete root.__agentForceOverlayDestroy;
    };

    function armDropMode() {
      const state = getGameState(root);
      if (state && state.monster) {
        setHelper("Agent Force is already engaged. Wait for all clear, then test another threat.");
        if (hint) {
          hint.textContent = "Agents are still engaged - wait for the all clear";
          hint.classList.add("deny");
        }
        return;
      }

      root.classList.add("is-targeting");
      root.classList.remove("is-complete");
      if (outcome) outcome.hidden = true;
      hideSolutionBrief();
      activeKills = state && typeof state.kills === "number" ? state.kills : activeKills;
      setHelper("Drop mode armed. Click an open spot in the real city.");
      if (targetingLabel) targetingLabel.textContent = "Click the city to drop " + selected.label;
      if (hint) {
        hint.textContent = "Click the city to drop " + selected.label;
        hint.classList.remove("deny");
      }
      startBattlePoll();
      focusCityCanvas();
      emit(root, "agent-force:drop-armed", selected);
    }

    function spawnSelected() {
      const game = root.__cityHeroGame;
      if (!game || typeof game.spawnAt !== "function") return;
      const spawned = game.spawnAt(selected.spawn[0], selected.spawn[1], "drop");
      if (spawned) handleMonsterDetected(game.state());
    }

    function handleCityPointerUp() {
      window.setTimeout(function () {
        const state = getGameState(root);
        if (state && state.monster) handleMonsterDetected(state);
      }, 80);
    }

    function handleCityKeydown(event) {
      if (!root.classList.contains("is-targeting")) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      spawnSelected();
    }

    function handleMonsterDetected(state) {
      if (seenMonster) return;
      seenMonster = true;
      root.classList.remove("is-targeting", "is-complete");
      root.classList.add("is-in-battle");
      if (outcome) outcome.hidden = true;
      activeKills = state && typeof state.kills === "number" ? state.kills : activeKills;
      showSolutionBrief(state);
      setHelper("Agent Force is engaging " + selected.label + ".");
      emit(root, "agent-force:monster-detected", selected);
      startBattlePoll();
    }

    function startBattlePoll() {
      stopPolling();
      pollTimer = window.setInterval(function () {
        const state = getGameState(root);
        if (!state) return;
        if (state.monster) {
          if (!seenMonster) handleMonsterDetected(state);
          updateSolutionBrief(state);
          return;
        }
        if (seenMonster && state.kills > activeKills) {
          showOutcome();
          stopPolling();
        }
      }, 250);
    }

    function showOutcome() {
      root.classList.remove("is-in-battle", "is-targeting");
      root.classList.add("is-complete");
      if (outcome) outcome.hidden = false;
      hideSolutionBrief();
      applyOutcomeCopy();
      deactivateCityScroll();
      const game = root.__cityHeroGame;
      if (game && typeof game.flyOutcome === "function") game.flyOutcome();
      if (hint) hint.textContent = "Threat neutralized - choose another problem or book a workflow audit";
      if (outcome && typeof outcome.focus === "function") outcome.focus({ preventScroll: true });
      emit(root, "agent-force:threat-neutralized", selected);
    }

    function handleCityPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      if (root.classList.contains("is-complete")) {
        mapClickReopenedSelection = true;
        reopenThreatSelection();
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!root.classList.contains("is-targeting") && !root.classList.contains("is-in-battle")) {
        event.stopImmediatePropagation();
        return;
      }
      activateCityScroll();
    }

    function guardCompletedMapClick(event) {
      if (!mapClickReopenedSelection && !root.classList.contains("is-complete")) return;
      mapClickReopenedSelection = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    function reopenThreatSelection() {
      root.classList.remove("is-complete", "is-in-battle", "is-targeting");
      if (outcome) outcome.hidden = true;
      hideSolutionBrief();
      seenMonster = false;
      activeKills = (getGameState(root) || {}).kills || 0;
      deactivateCityScroll();
      updateSelected();
      stopPolling();
    }

    function guardCityWheel(event) {
      if (cityScrollActive) return;
      event.stopImmediatePropagation();
    }

    function activateCityScroll() {
      if (cityScrollActive) return;
      cityScrollActive = true;
      root.classList.add("is-city-scroll-active");
      emit(root, "agent-force:city-scroll-active", selected);
    }

    function deactivateCityScroll() {
      if (!cityScrollActive) return;
      cityScrollActive = false;
      root.classList.remove("is-city-scroll-active");
      emit(root, "agent-force:city-scroll-inactive", selected);
    }

    function scrollPastHero(event) {
      if (event) event.preventDefault();
      deactivateCityScroll();
      root.classList.remove("is-targeting");
      stopPolling();
      const section = root.closest(".shopify-section") || root;
      const nextSection = section.nextElementSibling || root.nextElementSibling;
      const fallbackTop = root.getBoundingClientRect().bottom + window.scrollY;
      const targetTop = nextSection
        ? nextSection.getBoundingClientRect().top + window.scrollY
        : fallbackTop;
      const nextTop = Math.max(0, targetTop);
      window.scrollTo({ top: nextTop, behavior: "smooth" });
      window.setTimeout(function () {
        if (Math.abs(window.scrollY - nextTop) > 8) window.scrollTo(0, nextTop);
      }, 380);
      emit(root, "agent-force:scroll-down", selected);
    }

    function startReadyPoll() {
      readyTimer = window.setInterval(function () {
        if (!root.__cityHeroGame) return;
        window.clearInterval(readyTimer);
        readyTimer = 0;
        activeKills = (getGameState(root) || {}).kills || 0;
      }, 120);
    }

    function stopPolling() {
      if (!pollTimer) return;
      window.clearInterval(pollTimer);
      pollTimer = 0;
    }

    function updateSelected(options) {
      const settings = options || {};
      problemList.querySelectorAll("[data-problem-id]").forEach(function (button) {
        button.setAttribute("aria-pressed", String(button.dataset.problemId === selected.id));
      });
      if (selectedLabel) selectedLabel.textContent = selected.label;
      if (targetingLabel) targetingLabel.textContent = "Click the city to drop " + selected.label;
      if (settings.scroll !== false) scrollSelectedThreatIntoView();
      setHelper("Choose the threat, then click an open spot in the real city to drop the monster.");
      applyOutcomeCopy();
      updateSolutionBrief();
      emit(root, "agent-force:problem-selected", selected);
    }

    function handleThreatScroll() {
      if (syncingThreatScroll || !isMobileThreatCarousel()) return;
      scheduleThreatSelection();
    }

    function scheduleThreatSelection() {
      if (!isMobileThreatCarousel()) return;
      if (threatScrollTimer) window.clearTimeout(threatScrollTimer);
      threatScrollTimer = window.setTimeout(selectCenteredThreat, 90);
    }

    function selectCenteredThreat() {
      threatScrollTimer = 0;
      if (!isMobileThreatCarousel()) return;
      const cards = Array.from(problemList.querySelectorAll("[data-problem-id]"));
      if (!cards.length) return;
      const listRect = problemList.getBoundingClientRect();
      const listCenter = listRect.left + listRect.width / 2;
      const centered = cards.reduce(function (nearest, card) {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - listCenter);
        return !nearest || distance < nearest.distance ? { card, distance } : nearest;
      }, null);
      if (!centered || centered.card.dataset.problemId === selected.id) return;
      const next = PROBLEMS.find(function (problem) {
        return problem.id === centered.card.dataset.problemId;
      });
      if (!next) return;
      selected = next;
      updateSelected({ scroll: false });
    }

    function scrollSelectedThreatIntoView() {
      if (!isMobileThreatCarousel()) return;
      const card = problemList.querySelector('[data-problem-id="' + selected.id + '"]');
      if (!card) return;
      syncingThreatScroll = true;
      card.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      window.setTimeout(function () {
        syncingThreatScroll = false;
      }, 320);
    }

    function applyOutcomeCopy() {
      if (outcomeTitle) outcomeTitle.textContent = selected.title;
      if (outcomeBody) outcomeBody.textContent = selected.body;
      if (outcomeSimulation) {
        outcomeSimulation.textContent = "This mini game is the simple version: the monster represents the bottleneck you picked. In a real consult, Kachersoft maps the live business problem behind it and turns the fix into a working system.";
      }
      renderOutcomeSteps();
      metrics.forEach(function (metric, index) {
        metric.textContent = selected.metrics[index] || "";
      });
    }

    function renderOutcomeSteps() {
      if (!outcomeSteps) return;
      const steps = selected.steps || [];
      outcomeSteps.textContent = "";
      if (outcomeStepCount) outcomeStepCount.textContent = steps.length + " steps";
      steps.forEach(function (step, index) {
        const item = document.createElement("li");
        const number = document.createElement("span");
        const title = document.createElement("strong");
        number.textContent = String(index + 1).padStart(2, "0");
        title.textContent = step.title;
        item.append(number, title);
        outcomeSteps.appendChild(item);
      });
    }

    function setHelper(message) {
      if (helper) helper.textContent = message;
    }

    function showSolutionBrief(state) {
      if (!solutionBrief) return;
      solutionBrief.hidden = false;
      updateSolutionBrief(state);
    }

    function hideSolutionBrief() {
      if (solutionBrief) solutionBrief.hidden = true;
      if (solutionProgress) {
        solutionProgress.style.setProperty("--solution-progress", "0%");
        solutionProgress.style.setProperty("--solution-progress-ratio", "0");
      }
    }

    function updateSolutionBrief(state) {
      if (!solutionBrief) return;
      const steps = selected.steps || [];
      if (!steps.length) return;
      ensureProgressSteps(steps.length);

      const monster = state && state.monster;
      const maxHp = monster && monster.maxHp ? monster.maxHp : DEFAULT_MONSTER_HP;
      const hpRatio = monster ? clamp(monster.hp / maxHp, 0, 1) : 1;
      const progress = 1 - hpRatio;
      const stepIndex = Math.min(steps.length - 1, Math.floor(progress * steps.length));
      const activeStep = steps[stepIndex];

      if (solutionThreat) solutionThreat.textContent = selected.label;
      if (solutionStepCount) solutionStepCount.textContent = "Step " + (stepIndex + 1) + " / " + steps.length;
      if (solutionTitle) solutionTitle.textContent = activeStep.title;
      if (solutionBody) solutionBody.textContent = activeStep.body;
      if (solutionOutput) solutionOutput.textContent = activeStep.output;
      if (solutionProgress) {
        const stepRatio = steps.length > 1 ? stepIndex / (steps.length - 1) : 1;
        solutionProgress.style.setProperty("--solution-progress", (stepRatio * 100).toFixed(1) + "%");
        solutionProgress.style.setProperty("--solution-progress-ratio", stepRatio.toFixed(3));
        Array.from(solutionProgress.children).forEach(function (item, index) {
          item.classList.toggle("is-active", index === stepIndex);
          item.classList.toggle("is-complete", index < stepIndex);
        });
      }
    }

    function ensureProgressSteps(count) {
      if (!solutionProgress || renderedProgressSteps === count) return;
      renderedProgressSteps = count;
      solutionProgress.textContent = "";
      for (let index = 0; index < count; index += 1) {
        solutionProgress.appendChild(document.createElement("span"));
      }
    }

    function focusCityCanvas() {
      try {
        canvas.focus({ preventScroll: true });
      } catch (error) {
        canvas.focus();
      }
    }
  }

  function renderProblems(container, selectedId, onSelect) {
    PROBLEMS.forEach(function (problem) {
      const button = document.createElement("button");
      const label = document.createElement("strong");
      const detail = document.createElement("span");
      button.type = "button";
      button.className = "af-threat-button";
      button.dataset.problemId = problem.id;
      button.setAttribute("aria-pressed", String(problem.id === selectedId));
      label.textContent = problem.label;
      detail.textContent = problem.short;
      button.append(label, detail);
      button.addEventListener("click", function () {
        onSelect(problem);
      });
      container.appendChild(button);
    });
  }

  function getGameState(root) {
    const game = root.__cityHeroGame;
    return game && typeof game.state === "function" ? game.state() : null;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function isMobileThreatCarousel() {
    return window.matchMedia("(max-width: 900px)").matches;
  }

  function emit(root, name, detail) {
    root.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      detail
    }));
  }

  function boot(scope) {
    (scope || document).querySelectorAll("[data-agent-force-real-city]").forEach(initOverlay);
  }

  window.__agentForceOverlayBoot = boot;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    boot(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    event.target.querySelectorAll("[data-agent-force-real-city]").forEach(function (root) {
      if (root.__agentForceOverlayDestroy) root.__agentForceOverlayDestroy();
    });
  });
})();
